import React, { useCallback, useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input, Label } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast, Toaster } from "sonner";
import { useAuth, ROLE_LABELS } from "../contexts/AuthContext";
import { Plus, Search, Trash2, X, Save } from "lucide-react";

const ROLES = ["admin", "compliance_officer", "auditor", "employee"];

export default function UsersPage() {
  const { user: me } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("");
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ email: "", name: "", role: "employee", password: "", department: "", job_title: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (role) params.set("role", role);
      const { data } = await api.get(`/users?${params.toString()}`);
      setItems(data.items);
    } catch (e) {
      console.error("Failed to load users", e);
    }
    setLoading(false);
  }, [q, role]);

  useEffect(() => {
    load();
  }, [role, load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.patch(`/users/${editing.id}`, {
          name: form.name,
          role: form.role,
          department: form.department,
          job_title: form.job_title,
        });
        toast.success("User updated");
      } else {
        await api.post("/users", form);
        toast.success("User created");
      }
      setShowForm(false);
      setEditing(null);
      setForm({ email: "", name: "", role: "employee", password: "", department: "", job_title: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  const startEdit = (u) => {
    setEditing(u);
    setForm({
      email: u.email,
      name: u.name,
      role: u.role,
      password: "",
      department: u.department || "",
      job_title: u.job_title || "",
    });
    setShowForm(true);
  };

  const del = async (u) => {
    if (!window.confirm(`Delete user ${u.email}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  const roleColor = {
    admin: "critical",
    compliance_officer: "primary",
    auditor: "info",
    employee: "neutral",
  };

  return (
    <div className="space-y-6">
      <Toaster richColors position="top-right" />

      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Users</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage user accounts, roles and permissions.
          </p>
        </div>
        {me?.role === "admin" && (
          <Button
            data-testid="users-new"
            onClick={() => {
              setEditing(null);
              setForm({ email: "", name: "", role: "employee", password: "", department: "", job_title: "" });
              setShowForm(true);
            }}
          >
            <Plus className="w-4 h-4" />
            New user
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                data-testid="users-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && load()}
                className="pl-9"
                placeholder="Search by email or name…"
              />
            </div>
            <select
              data-testid="users-role-filter"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="h-10 w-full sm:w-48 rounded-md border border-border bg-card px-3 text-sm"
            >
              <option value="">All roles</option>
              {ROLES.map((r) => (
                <option key={r} value={r}>{ROLE_LABELS[r]}</option>
              ))}
            </select>
            <Button variant="outline" data-testid="users-search-btn" onClick={load}>Search</Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-t border-b border-border bg-muted/40">
                  <th className="text-left py-2.5 px-6 label-eyebrow">Name</th>
                  <th className="text-left py-2.5 px-6 label-eyebrow">Email</th>
                  <th className="text-left py-2.5 px-6 label-eyebrow">Role</th>
                  <th className="text-left py-2.5 px-6 label-eyebrow">Department</th>
                  <th className="text-left py-2.5 px-6 label-eyebrow">Status</th>
                  <th className="text-right py-2.5 px-6 label-eyebrow">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-muted-foreground">Loading…</td>
                  </tr>
                )}
                {!loading && items.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-muted-foreground">No users found.</td>
                  </tr>
                )}
                {items.map((u) => (
                  <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-border hover:bg-muted/40 transition-colors">
                    <td className="py-3 px-6 font-medium">{u.name}</td>
                    <td className="py-3 px-6 text-muted-foreground">{u.email}</td>
                    <td className="py-3 px-6">
                      <Badge variant={roleColor[u.role]}>{ROLE_LABELS[u.role]}</Badge>
                    </td>
                    <td className="py-3 px-6 text-muted-foreground">{u.department || "—"}</td>
                    <td className="py-3 px-6">
                      <Badge variant={u.is_active ? "success" : "neutral"}>
                        {u.is_active ? "Active" : "Disabled"}
                      </Badge>
                    </td>
                    <td className="py-3 px-6 text-right">
                      {me?.role === "admin" && (
                        <div className="inline-flex gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            data-testid={`user-edit-${u.id}`}
                            onClick={() => startEdit(u)}
                          >
                            Edit
                          </Button>
                          {u.id !== me.id && (
                            <Button
                              size="sm"
                              variant="destructive"
                              data-testid={`user-delete-${u.id}`}
                              onClick={() => del(u)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div
            className="w-full max-w-lg card-surface animate-fade-in"
            onClick={(e) => e.stopPropagation()}
            data-testid="user-form-modal"
          >
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h3 className="text-base font-semibold">{editing ? "Edit user" : "Create user"}</h3>
              <button onClick={() => setShowForm(false)} className="p-1 rounded hover:bg-muted">
                <X className="w-4 h-4" />
              </button>
            </div>
            <form onSubmit={submit} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label>Full name</Label>
                <Input
                  data-testid="user-form-name"
                  className="mt-1.5"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  data-testid="user-form-email"
                  className="mt-1.5"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  disabled={!!editing}
                  required
                />
              </div>
              <div>
                <Label>Role</Label>
                <select
                  data-testid="user-form-role"
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                  className="h-10 w-full rounded-md border border-border bg-card px-3 text-sm mt-1.5"
                >
                  {ROLES.map((r) => (
                    <option key={r} value={r}>{ROLE_LABELS[r]}</option>
                  ))}
                </select>
              </div>
              {!editing && (
                <div className="md:col-span-2">
                  <Label>Password (min 8 chars)</Label>
                  <Input
                    data-testid="user-form-password"
                    className="mt-1.5"
                    type="password"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    required
                  />
                </div>
              )}
              <div>
                <Label>Department</Label>
                <Input
                  className="mt-1.5"
                  value={form.department}
                  onChange={(e) => setForm({ ...form, department: e.target.value })}
                />
              </div>
              <div>
                <Label>Job title</Label>
                <Input
                  className="mt-1.5"
                  value={form.job_title}
                  onChange={(e) => setForm({ ...form, job_title: e.target.value })}
                />
              </div>
              <div className="md:col-span-2 flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
                <Button type="submit" data-testid="user-form-submit">
                  <Save className="w-4 h-4" />
                  {editing ? "Save changes" : "Create user"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
